import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import SlotPicker from '../SlotPicker';
import VehicleSelector from '../VehicleSelector';

const API = process.env.REACT_APP_API_URL || '/api';

const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '2rem 1rem', overflowY: 'auto' };
const modalBox = { background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0', width: '100%', maxWidth: '640px', margin: '2rem auto' };
const headerStyle = { padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(148,163,184,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const bodyStyle = { padding: '1.5rem' };
const footerStyle = { padding: '1rem 1.5rem', borderTop: '1px solid rgba(148,163,184,0.15)', display: 'flex', justifyContent: 'space-between', gap: '0.75rem' };
const labelSm = { fontSize: '0.75rem', color: '#94A3B8', display: 'block', marginBottom: '0.25rem' };
const sectionHead = { color: '#D42B2B', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem', marginTop: '1rem' };

export default function CreateAppointmentModal({ onClose, onCreated, t }) {
  const { language } = useLanguage();
  const ca = t.admin?.createAppointment || {};

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step 1 — Customer
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ first_name: '', last_name: '', phone: '', email: '', preferred_language: 'fr' });
  const [customerError, setCustomerError] = useState(null);
  const searchTimer = useRef(null);

  // Step 2 — Vehicle
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showNewVehicle, setShowNewVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ make: '', model: '', year: new Date().getFullYear(), license_plate: '' });
  const [skipVehicle, setSkipVehicle] = useState(false);

  // Step 3 — Services
  const [allServices, setAllServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  // Step 4 — Date/Time
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [allowPast, setAllowPast] = useState(false);

  // Step 5 — Confirm
  const [initialStatus, setInitialStatus] = useState('confirmed');
  const [sendNotification, setSendNotification] = useState(true);
  const [skipDeposit, setSkipDeposit] = useState(false);
  const [allowOverbook, setAllowOverbook] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  // ── Load services on mount ─────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/services`).then(res => {
      setAllServices(Array.isArray(res.data) ? res.data : res.data.services || []);
    }).catch(() => {});
  }, []);

  // ── Customer search ────────────────────────────────────────
  const handleSearch = useCallback((val) => {
    setCustomerSearch(val);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 2) { setCustomerResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/admin/customers?q=${encodeURIComponent(val.trim())}&limit=10`);
        const list = res.data.data || res.data.customers || res.data;
        setCustomerResults(Array.isArray(list) ? list : []);
      } catch { setCustomerResults([]); }
      setSearching(false);
    }, 300);
  }, []);

  const selectCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomerSearch('');
    setCustomerResults([]);
    setShowNewForm(false);
    setCustomerError(null);
  };

  // ── Create walk-in customer ────────────────────────────────
  const createCustomer = async () => {
    setCustomerError(null);
    if (!newCustomer.first_name.trim() || !newCustomer.last_name.trim() || !newCustomer.phone.trim()) {
      setCustomerError(ca.requiredFields);
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/customers`, newCustomer);
      selectCustomer(res.data.customer);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data.existing_customer) {
        setCustomerError(ca.duplicatePhone);
        setCustomerResults([err.response.data.existing_customer]);
        setShowNewForm(false);
      } else {
        setCustomerError(err.response?.data?.error || ca.errorCreating);
      }
    }
  };

  // ── Load customer vehicles when customer selected ──────────
  useEffect(() => {
    if (!selectedCustomer) return;
    axios.get(`${API}/admin/customers/${selectedCustomer.id}/profile`)
      .then(res => {
        setCustomerVehicles(res.data.vehicles || []);
        // Auto-check send notification based on email
        const isPlaceholder = selectedCustomer.email?.includes('@cjpa.local');
        setSendNotification(!isPlaceholder);
      })
      .catch(() => setCustomerVehicles([]));
  }, [selectedCustomer]);

  // ── Service selection ──────────────────────────────────────
  const toggleService = (id) => {
    setSelectedServiceIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : prev.length < 6 ? [...prev, id] : prev
    );
  };

  const selectedServices = allServices.filter(s => selectedServiceIds.includes(s.id));
  const totalDuration = selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 60), 0);
  const totalDepositAmount = selectedServices.reduce((sum, s) => sum + (parseFloat(s.deposit_amount) || 0), 0);
  const priceMin = selectedServices.reduce((sum, s) => sum + (parseFloat(s.price_min) || 0), 0);
  const priceMax = selectedServices.reduce((sum, s) => sum + (parseFloat(s.price_max) || 0), 0);

  // ── Book now helper ────────────────────────────────────────
  const bookNow = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0'), d = String(now.getDate()).padStart(2, '0');
    setSelectedDate(new Date(y, now.getMonth(), now.getDate()));
    // Round up to next 15-min slot
    const mins = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 15) * 15;
    const h = Math.floor(mins / 60), mi = mins % 60;
    setSelectedTime(`${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`);
  };

  // ── Submit ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const dateStr = selectedDate instanceof Date
      ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
      : String(selectedDate).slice(0, 10);

    const payload = {
      user_id: selectedCustomer.id,
      service_ids: selectedServiceIds,
      appointment_date: dateStr,
      appointment_time: selectedTime,
      notes: null,
      admin_notes: adminNotes || null,
      initial_status: initialStatus,
      skip_deposit: skipDeposit,
      allow_past: allowPast,
      allow_overbook: allowOverbook,
      suppress_notifications: !sendNotification,
      source: allowPast ? 'admin_retroactive' : 'admin_walkin'
    };

    if (selectedVehicleId) {
      payload.vehicle_id = selectedVehicleId;
    } else if (showNewVehicle && newVehicle.make && newVehicle.model) {
      payload.vehicle = newVehicle;
    }

    try {
      await axios.post(`${API}/admin/appointments`, payload);
      onCreated?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || ca.errorCreating;
      if (msg.includes('fully booked')) {
        setError(msg);
        setAllowOverbook(true); // Show the overbook option
      } else {
        setError(msg);
      }
    }
    setSubmitting(false);
  };

  // ── Step validation ────────────────────────────────────────
  const canNext = () => {
    switch (step) {
      case 1: return !!selectedCustomer;
      case 2: return skipVehicle || selectedVehicleId || (showNewVehicle && newVehicle.make && newVehicle.model);
      case 3: return selectedServiceIds.length > 0;
      case 4: return selectedDate && selectedTime;
      case 5: return true;
      default: return false;
    }
  };

  const stepTitles = [ca.step1, ca.step2, ca.step3, ca.step4, ca.step5];

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h5 style={{ fontFamily: "'Rajdhani', sans-serif", color: '#D42B2B', margin: 0, fontSize: '1.1rem' }}>
            {ca.title} — {stepTitles[step - 1]} ({step}/5)
          </h5>
          <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label={t.common.close}></button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {error && <div className="alert alert-danger" style={{ fontSize: '0.8rem' }}>{error}</div>}

          {/* ── STEP 1: Customer ──────────────────────────── */}
          {step === 1 && (
            <div>
              {selectedCustomer ? (
                <div style={{ background: '#111827', padding: '1rem', border: '1px solid rgba(148,163,184,0.2)', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong>
                      <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
                        {selectedCustomer.phone} {selectedCustomer.email && !selectedCustomer.email.includes('@cjpa.local') && `· ${selectedCustomer.email}`}
                      </div>
                    </div>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => { setSelectedCustomer(null); setCustomerVehicles([]); }} style={{ fontSize: '0.7rem' }}>
                      {ca.changeCustomer}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <label htmlFor="cust-search" style={labelSm}>{ca.searchCustomer}</label>
                  <input id="cust-search" type="text" className="form-control form-control-sm" placeholder={ca.searchPlaceholder}
                    value={customerSearch} onChange={e => handleSearch(e.target.value)} autoFocus />
                  {searching && <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: '0.5rem' }}>{t.common.loading}</div>}

                  {customerResults.length > 0 && (
                    <div style={{ marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                      {customerResults.map(c => (
                        <div key={c.id} onClick={() => selectCustomer(c)}
                          style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid rgba(148,163,184,0.1)', fontSize: '0.85rem' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <strong>{c.first_name} {c.last_name}</strong>
                          <span style={{ color: '#94A3B8', marginLeft: '0.5rem' }}>{c.phone} {c.email && !c.email.includes('@cjpa.local') ? `· ${c.email}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {customerSearch.length >= 2 && customerResults.length === 0 && !searching && (
                    <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: '0.5rem' }}>{ca.noResults}</div>
                  )}

                  <button className="btn btn-outline-primary btn-sm" onClick={() => setShowNewForm(!showNewForm)}
                    style={{ marginTop: '1rem', fontSize: '0.75rem' }}>
                    + {ca.newCustomer}
                  </button>

                  {showNewForm && (
                    <div style={{ background: '#111827', padding: '1rem', marginTop: '0.75rem', border: '1px solid rgba(148,163,184,0.15)' }}>
                      <div style={sectionHead}>{ca.newCustomer}</div>
                      {customerError && <div className="alert alert-danger" style={{ fontSize: '0.75rem', padding: '0.5rem' }}>{customerError}</div>}
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <label htmlFor="nc-fn" style={labelSm}>{ca.firstName} *</label>
                          <input id="nc-fn" className="form-control form-control-sm" value={newCustomer.first_name}
                            onChange={e => setNewCustomer(p => ({ ...p, first_name: e.target.value }))} />
                        </div>
                        <div className="col-6">
                          <label htmlFor="nc-ln" style={labelSm}>{ca.lastName} *</label>
                          <input id="nc-ln" className="form-control form-control-sm" value={newCustomer.last_name}
                            onChange={e => setNewCustomer(p => ({ ...p, last_name: e.target.value }))} />
                        </div>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <label htmlFor="nc-ph" style={labelSm}>{ca.phone} *</label>
                          <input id="nc-ph" className="form-control form-control-sm" value={newCustomer.phone}
                            onChange={e => setNewCustomer(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                        <div className="col-6">
                          <label htmlFor="nc-em" style={labelSm}>{ca.email} ({ca.optional})</label>
                          <input id="nc-em" type="email" className="form-control form-control-sm" value={newCustomer.email}
                            onChange={e => setNewCustomer(p => ({ ...p, email: e.target.value }))} />
                        </div>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={createCustomer} style={{ fontSize: '0.75rem' }}>
                        {ca.createCustomer}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── STEP 2: Vehicle ───────────────────────────── */}
          {step === 2 && (
            <div>
              {customerVehicles.length > 0 && (
                <>
                  <div style={sectionHead}>{ca.savedVehicles}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                    {customerVehicles.map(v => (
                      <div key={v.id} onClick={() => { setSelectedVehicleId(v.id); setShowNewVehicle(false); setSkipVehicle(false); }}
                        style={{
                          background: selectedVehicleId === v.id ? 'rgba(212,43,43,0.15)' : '#111827',
                          border: selectedVehicleId === v.id ? '1px solid #D42B2B' : '1px solid rgba(148,163,184,0.15)',
                          padding: '0.75rem', cursor: 'pointer', fontSize: '0.85rem'
                        }}>
                        <strong>{v.year} {v.make} {v.model}</strong>
                        {v.license_plate && <span style={{ color: '#94A3B8', marginLeft: '0.5rem' }}>{v.license_plate}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {customerVehicles.length === 0 && !showNewVehicle && (
                <div style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '1rem' }}>{ca.noVehicles}</div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className={`btn btn-sm ${showNewVehicle ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => { setShowNewVehicle(!showNewVehicle); setSelectedVehicleId(null); setSkipVehicle(false); }}
                  style={{ fontSize: '0.75rem' }}>
                  + {ca.addVehicle}
                </button>
                <button className={`btn btn-sm ${skipVehicle ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => { setSkipVehicle(!skipVehicle); setSelectedVehicleId(null); setShowNewVehicle(false); }}
                  style={{ fontSize: '0.75rem' }}>
                  {ca.skipVehicle}
                </button>
              </div>

              {showNewVehicle && (
                <div style={{ marginTop: '1rem' }}>
                  <VehicleSelector value={newVehicle} onChange={setNewVehicle} t={t} showVin showColor />
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Services ──────────────────────────── */}
          {step === 3 && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.5rem' }}>
                {allServices.filter(s => s.is_active).map(svc => {
                  const selected = selectedServiceIds.includes(svc.id);
                  return (
                    <div key={svc.id} onClick={() => toggleService(svc.id)} style={{
                      background: selected ? 'rgba(212,43,43,0.15)' : '#111827',
                      border: selected ? '1px solid #D42B2B' : '1px solid rgba(148,163,184,0.15)',
                      padding: '0.75rem', cursor: 'pointer'
                    }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{language === 'fr' ? (svc.name_fr || svc.name) : (svc.name_en || svc.name)}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                        {svc.duration_minutes} min · ${svc.price_min}–${svc.price_max}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selectedServiceIds.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#111827', border: '1px solid rgba(148,163,184,0.15)', fontSize: '0.8rem' }}>
                  <strong>{selectedServiceIds.length} {ca.servicesSelected}</strong> · {totalDuration} min · ${priceMin.toFixed(2)}–${priceMax.toFixed(2)}
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: Date & Time ───────────────────────── */}
          {step === 4 && (
            <div>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button className="btn btn-outline-primary btn-sm" onClick={bookNow} style={{ fontSize: '0.75rem' }}>
                  {ca.bookNow}
                </button>
                <div className="form-check form-switch" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input className="form-check-input" type="checkbox" checked={allowPast} onChange={e => setAllowPast(e.target.checked)} id="allow-past" />
                  <label className="form-check-label" htmlFor="allow-past" style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{ca.allowPast}</label>
                </div>
              </div>
              <SlotPicker
                serviceIds={selectedServiceIds}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onDateChange={setSelectedDate}
                onTimeChange={setSelectedTime}
                language={language}
                t={t}
              />
            </div>
          )}

          {/* ── STEP 5: Confirm ───────────────────────────── */}
          {step === 5 && (
            <div>
              {/* Summary */}
              <div style={{ background: '#111827', padding: '1rem', border: '1px solid rgba(148,163,184,0.15)', marginBottom: '1rem', fontSize: '0.85rem' }}>
                <div><strong>{ca.customer}:</strong> {selectedCustomer?.first_name} {selectedCustomer?.last_name}</div>
                <div><strong>{ca.servicesLabel}:</strong> {selectedServices.map(s => language === 'fr' ? (s.name_fr || s.name) : (s.name_en || s.name)).join(' + ')}</div>
                {selectedDate && <div><strong>{ca.dateTime}:</strong> {selectedDate instanceof Date ? selectedDate.toLocaleDateString() : selectedDate} {ca.at} {selectedTime}</div>}
                {(selectedVehicleId || (showNewVehicle && newVehicle.make)) && (
                  <div><strong>{ca.vehicleLabel}:</strong> {selectedVehicleId ? customerVehicles.find(v => v.id === selectedVehicleId)?.make + ' ' + customerVehicles.find(v => v.id === selectedVehicleId)?.model : `${newVehicle.year} ${newVehicle.make} ${newVehicle.model}`}</div>
                )}
                <div style={{ color: '#94A3B8', marginTop: '0.25rem' }}>{totalDuration} min · ${priceMin.toFixed(2)}–${priceMax.toFixed(2)}</div>
              </div>

              {/* Admin options */}
              <div style={sectionHead}>{ca.adminOptions}</div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelSm}>{ca.status}</label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {['pending', 'confirmed', 'in_progress'].map(s => (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                      <input type="radio" name="status" value={s} checked={initialStatus === s} onChange={() => setInitialStatus(s)} />
                      {t.dashboard?.status?.[s] || s}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-check" style={{ marginBottom: '0.5rem' }}>
                <input className="form-check-input" type="checkbox" checked={sendNotification} onChange={e => setSendNotification(e.target.checked)} id="send-notif" />
                <label className="form-check-label" htmlFor="send-notif" style={{ fontSize: '0.8rem' }}>{ca.sendNotification}</label>
              </div>

              {totalDepositAmount > 0 && (
                <div className="form-check" style={{ marginBottom: '0.5rem' }}>
                  <input className="form-check-input" type="checkbox" checked={skipDeposit} onChange={e => setSkipDeposit(e.target.checked)} id="skip-deposit" />
                  <label className="form-check-label" htmlFor="skip-deposit" style={{ fontSize: '0.8rem' }}>{ca.skipDeposit} (${totalDepositAmount.toFixed(2)})</label>
                </div>
              )}

              {error?.includes('fully booked') && (
                <div className="form-check" style={{ marginBottom: '0.5rem' }}>
                  <input className="form-check-input" type="checkbox" checked={allowOverbook} onChange={e => setAllowOverbook(e.target.checked)} id="overbook" />
                  <label className="form-check-label" htmlFor="overbook" style={{ fontSize: '0.8rem', color: '#F97316' }}>{ca.overbook}</label>
                </div>
              )}

              <div style={{ marginTop: '0.75rem' }}>
                <label htmlFor="admin-notes" style={labelSm}>{ca.adminNotes}</label>
                <textarea id="admin-notes" className="form-control form-control-sm" rows="2" value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)} placeholder={ca.adminNotesPlaceholder} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          {step > 1 ? (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep(step - 1)}>{t.common.back}</button>
          ) : (
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>{t.common.cancel}</button>
          )}

          {step < 5 ? (
            <button className="btn btn-primary btn-sm" disabled={!canNext()} onClick={() => setStep(step + 1)}>{t.common.next}</button>
          ) : (
            <button className="btn btn-primary btn-sm" disabled={submitting} onClick={handleSubmit}>
              {submitting ? t.common.loading : ca.createAppointment}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
